import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { CampusExercise } from '../entities/CampusExercise.js';
import { CampusLesson } from '../entities/CampusLesson.js';
import { CampusSection } from '../entities/CampusSection.js';
import { duplicateContent } from '../server/duplicateContent.js';

const resolveMoveLessonToSection = (lessonID: string, fromSectionID: string, toSectionID: string) => {
  return async (gqlFields: void) => {
    const lesson = await getByID(CampusLesson, lessonID)({ _id: 1 });
    const fromSection = await getByID(CampusSection, fromSectionID)({ lessonIDs: 1 });
    const toSection = await getByID(CampusSection, toSectionID)({ lessonIDs: 1 });

    if (!lesson) throw new Error('Invalid lessonID given');
    if (!fromSection) throw new Error('Invalid fromSectionID given');
    if (!toSection) throw new Error('Invalid toSectionID given');

    const lessonIndexInFromSection = fromSection.lessonIDs?.indexOf(lesson._id);
    if (!lessonIndexInFromSection || lessonIndexInFromSection === -1)
      throw new Error('Given lesson not in its section');
    fromSection.lessonIDs?.splice(lessonIndexInFromSection, 1);
    toSection.lessonIDs?.push(lesson._id);

    await fromSection.save();
    await toSection.save();
    return '';
  };
};

const moveLessonToSection = registerMutation({
  type: () => String,
  operationName: 'moveLessonToSection',
  params: [
    { type: () => String, name: 'lessonID' },
    { type: () => String, name: 'fromSectionID' },
    { type: () => String, name: 'toSectionID' },
  ],
  resolve: resolveMoveLessonToSection,
});

namespace lessonAPI {
  export const moveToSection = moveLessonToSection;
}

const resolveDuplicateExercise = (exerciseID: string, lessonID: string) => {
  return async () => {
    const exerciseToDuplicate = await getByID(CampusExercise, exerciseID)(CampusExercise.allFields);
    const lessonForTheNewDuplicate = await getByID(CampusLesson, lessonID)({ exerciseIDs: 1 });

    if (!exerciseToDuplicate) throw new Error('Invalid exerciseID given for duplication');
    if (!lessonForTheNewDuplicate) throw new Error('Invalid lessonID given for duplication');

    const newContentID = await duplicateContent(exerciseToDuplicate as CampusExercise);

    if (!newContentID?.length) throw new Error(`Something went wrong while copying content with id ${exerciseID}`);
    lessonForTheNewDuplicate.exerciseIDs.push(newContentID);
    await lessonForTheNewDuplicate.save();
    return newContentID;
  };
};

const duplicateExercise = registerMutation({
  type: () => String,
  operationName: 'duplicateExercise',
  params: [
    { type: () => String, name: 'exerciseID' },
    { type: () => String, name: 'lessonID' },
  ],
  resolve: resolveDuplicateExercise,
});

const resolveMoveExerciseToLesson = (exerciseID: string, fromLessonID: string, toLessonID: string) => {
  return async () => {
    const exercise = await getByID(CampusExercise, exerciseID)({ _id: 1 });
    const fromLesson = await getByID(CampusLesson, fromLessonID)({ exerciseIDs: 1 });
    const toLesson = await getByID(CampusLesson, toLessonID)({ exerciseIDs: 1 });

    if (!exercise) throw new Error('Invalid contentID given');
    if (!fromLesson) throw new Error('Invalid fromLessonID given');
    if (!toLesson) throw new Error('Invalid toLessonID given');

    const exerciseIndexInFromLesson = fromLesson.exerciseIDs.indexOf(exercise._id);
    if (exerciseIndexInFromLesson === -1) throw new Error('Given content not in fromLesson');
    fromLesson.exerciseIDs.splice(exerciseIndexInFromLesson, 1);
    toLesson.exerciseIDs.push(exercise._id);

    await fromLesson.save();
    await toLesson.save();
    return '';
  };
};

const moveExerciseToLesson = registerMutation({
  type: () => String,
  operationName: 'moveExerciseToLesson',
  params: [
    { type: () => String, name: 'exerciseID' },
    { type: () => String, name: 'fromLessonID' },
    { type: () => String, name: 'toLessonID' },
  ],
  resolve: resolveMoveExerciseToLesson,
});

namespace exerciseAPI {
  export const duplicate = duplicateExercise;
  export const moveToLesson = moveExerciseToLesson;
}

export namespace manipulationAPI {
  export const lesson = lessonAPI;
  export const exercise = exerciseAPI;
}
